import React, { useState, useEffect } from 'react';
import { MessageSquare, Smartphone, Send, Users, Bot, Clock, Rocket, Settings, ExternalLink, Zap, Radio } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminWaSender = () => {
  const [config, setConfig] = useState({ api_url: '', api_key: '', device_id: '' });
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const r = await axios.get(`${API}/admin/wasender/config`);
        if (r.data.api_url) {
          setConfig(r.data);
          setConnected(!!r.data.api_url && !!r.data.api_key);
        }
      } catch (e) {}
      setLoading(false);
    };
    fetch();
  }, []);

  const saveConfig = async () => {
    try {
      await axios.post(`${API}/admin/wasender/config`, config);
      setConnected(!!config.api_url && !!config.api_key);
      toast.success('הגדרות WaSender נשמרו');
    } catch (e) { toast.error('שגיאה בשמירה'); }
  };

  const quickSend = async (phone, message) => {
    try {
      await axios.post(`${API}/admin/wasender/send`, { phone, message });
      toast.success('הודעה נשלחה');
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה בשליחה'); }
  };

  const [quickPhone, setQuickPhone] = useState('');
  const [quickMsg, setQuickMsg] = useState('');

  const features = [
    { icon: Smartphone, title: 'מכשירים', desc: 'חיבור וניהול מכשירי WhatsApp', color: 'bg-blue-100 text-blue-600' },
    { icon: Send, title: 'שליחה בודדת', desc: 'שלח הודעה ללקוח ספציפי', color: 'bg-green-100 text-green-600' },
    { icon: Rocket, title: 'קמפיינים', desc: 'שליחת הודעות מרובות', color: 'bg-purple-100 text-purple-600' },
    { icon: Bot, title: 'בוט AI', desc: 'מענה אוטומטי חכם', color: 'bg-amber-100 text-amber-600' },
    { icon: Clock, title: 'הודעות מתוזמנות', desc: 'תזמון שליחה אוטומטי', color: 'bg-cyan-100 text-cyan-600' },
    { icon: Users, title: 'אנשי קשר', desc: 'ניהול רשימת לקוחות', color: 'bg-rose-100 text-rose-600' },
  ];

  if (loading) return null;

  return (
    <div dir="rtl" data-testid="admin-wasender-section">
      <h2 className="text-xl font-black text-[#2C3E2D] mb-4" style={{ fontFamily: 'Heebo, sans-serif' }}>
        <MessageSquare className="w-5 h-5 inline ml-2 text-green-500" />ניהול וואטסאפ בוט (WaSender)
      </h2>

      {/* Connection Status */}
      <div className={`rounded-xl p-4 mb-4 flex items-center justify-between ${connected ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
        <div className="flex items-center gap-3">
          <Radio className={`w-5 h-5 ${connected ? 'text-green-500' : 'text-amber-500'}`} />
          <div>
            <p className={`font-bold text-sm ${connected ? 'text-green-700' : 'text-amber-700'}`}>
              {connected ? 'מחובר ל-WaSender API' : 'לא מחובר - הגדר API'}
            </p>
            {connected && <p className="text-green-600 text-xs">{config.api_url}</p>}
          </div>
        </div>
        {connected && (
          <span className="flex items-center gap-1 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
            <Zap className="w-3 h-3" /> פעיל
          </span>
        )}
      </div>

      {/* API Config */}
      <div className="bg-white rounded-xl p-4 border border-stone-200 mb-4">
        <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
          <Settings className="w-4 h-4 text-stone-400" /> הגדרות חיבור
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-stone-400 block mb-1">API URL</label>
            <input
              value={config.api_url}
              onChange={e => setConfig({...config, api_url: e.target.value})}
              placeholder="https://api.wasender.com"
              className="border rounded-lg px-3 py-2 text-sm w-full"
              dir="ltr"
              data-testid="wasender-api-url"
            />
          </div>
          <div>
            <label className="text-xs text-stone-400 block mb-1">API Key</label>
            <input
              value={config.api_key}
              onChange={e => setConfig({...config, api_key: e.target.value})}
              placeholder="your-api-key"
              className="border rounded-lg px-3 py-2 text-sm w-full"
              dir="ltr"
              type="password"
              data-testid="wasender-api-key"
            />
          </div>
          <div>
            <label className="text-xs text-stone-400 block mb-1">Device ID</label>
            <input
              value={config.device_id}
              onChange={e => setConfig({...config, device_id: e.target.value})}
              placeholder="device-id"
              className="border rounded-lg px-3 py-2 text-sm w-full"
              dir="ltr"
              data-testid="wasender-device-id"
            />
          </div>
        </div>
        <button onClick={saveConfig} className="mt-3 bg-[#D97706] hover:bg-[#B45309] text-white rounded-lg px-4 py-2 text-sm font-bold" data-testid="wasender-save-btn">
          שמור הגדרות
        </button>
      </div>

      {/* Quick Send */}
      {connected && (
        <div className="bg-white rounded-xl p-4 border border-stone-200 mb-4">
          <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
            <Send className="w-4 h-4 text-green-500" /> שליחה מהירה
          </h3>
          <div className="flex gap-2 mb-2">
            <input
              value={quickPhone}
              onChange={e => setQuickPhone(e.target.value)}
              placeholder="מספר טלפון"
              className="border rounded-lg px-3 py-2 text-sm flex-1"
              dir="ltr"
              data-testid="wasender-quick-phone"
            />
            <button
              onClick={() => { if (quickPhone && quickMsg) quickSend(quickPhone, quickMsg); }}
              className="bg-green-500 hover:bg-green-600 text-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1"
              data-testid="wasender-quick-send"
            >
              <Send className="w-4 h-4" /> שלח
            </button>
          </div>
          <textarea
            value={quickMsg}
            onChange={e => setQuickMsg(e.target.value)}
            placeholder="הודעה..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
            rows={3}
            data-testid="wasender-quick-msg"
          />
        </div>
      )}

      {/* Features Grid */}
      <h3 className="font-bold text-sm mb-3 text-stone-600">יכולות המערכת</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {features.map((f, i) => (
          <div key={i} className="bg-white rounded-xl p-4 border border-stone-100 text-center hover:shadow-md transition-shadow" data-testid={`wasender-feature-${i}`}>
            <div className={`w-12 h-12 rounded-xl ${f.color} flex items-center justify-center mx-auto mb-2`}>
              <f.icon className="w-6 h-6" />
            </div>
            <p className="font-bold text-sm text-stone-700">{f.title}</p>
            <p className="text-xs text-stone-400 mt-1">{f.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminWaSender;
